import { BSON } from 'bson';
import { Buffer } from 'buffer';
import { pack as packMsgpack, unpack as unpackMsgpack } from 'msgpackr';
import { useApiConnectivityStore } from 'web/stores/apiConnectivitityStore';

export const sendGetWithoutJson = async (url: string) : Promise<void> => {
    await makeRequest(url, 'GET');
}

export const sendGet = async <T = any> (url: string, signal?: AbortSignal) : Promise<T> => {
    const reply = await makeRequest(url, 'GET', undefined, "json", undefined, signal);
    return await reply.json();
}

export const sendGetBlob = async (url: string): Promise<Blob> => {
    const reply = await makeRequest(url, 'GET')
    if (!reply.ok) {
      throw new Error(`GET ${url} failed: ${reply.status} ${reply.statusText}`)
    }
    return await reply.blob()
  }

export const sendGetBson = async <T = any> (url: string) : Promise<T> => {
    const reply = await makeRequest(url, 'GET', undefined, "json", "application/bson");
    return await reply.json();
}

export const sendGetMsgpack = async <T = any> (url: string) : Promise<T> => {
    const reply = await makeRequest(url, 'GET', undefined, "json", "application/msgpack");
    return await reply.json();
}

export const sendGetWithTotal = async <T = any> (url: string) : Promise<[T, number]> => {
    const reply = await makeRequest(url, 'GET');
    return [await reply.json(), reply.headers.get('X-Total-Count') ? parseInt(reply.headers.get('X-Total-Count')!) : 0];
}

export const sendPostForJson = async (url: string, body?: any) : Promise<any> => {
    const reply = await makeRequest(url, 'POST', body);
    return await reply.json();
}

export const sendPostForm = async (url: string, body?: any) : Promise<void> => {
    await makeRequest(url, 'POST', body, "form");
}

export const sendPostFormForJson = async (url: string, body?: any) : Promise<any> => {
    const reply = await makeRequest(url, 'POST', body, "form");
    return await reply.json();
}

export async function sendPostFormWithFilesForJson<T>(url: string, data: Record<string, any>): Promise<T> {
    const formData = new FormData();
    
    // Properly append each field to FormData
    Object.entries(data).forEach(([key, value]) => {
        if (value instanceof File) {
            formData.append(key, value, value.name);
        } else if (value instanceof Blob) {
            formData.append(key, value);
        } else if (typeof value === 'object' && value !== null) {
            formData.append(key, JSON.stringify(value));
        } else {
            formData.append(key, String(value));
        }
    });

    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
    }

    return response.json();
}

export const sendPost = async (url: string, body?: any) : Promise<void> => {
    await makeRequest(url, 'POST', body);
}

export const sendPostForBlob = async (url: string, body?: any): Promise<Blob> => {
    // Non-ok replies already throw (with the parsed error body) inside makeRequest.
    const reply = await makeRequest(url, 'POST', body);
    return await reply.blob();
}

export const sendPutForm = async (url: string, body?: any) : Promise<void> => {
    await makeRequest(url, 'PUT', body, "form");
}

export const sendPut = async (url: string, body: any) : Promise<void> => {
    await makeRequest(url, 'PUT', body);
}

export const sendPutForJson = async (url: string, body: any) : Promise<any> => {
    const reply = await makeRequest(url, 'PUT', body);
    return await reply.json();
}

export const sendPatch = async (url: string, body: any) : Promise<void> => {
    await makeRequest(url, 'PATCH', body);
}

export const sendPatchForJson = async (url: string, body: any) : Promise<any> => {
    const reply = await makeRequest(url, 'PATCH', body);
    return await reply.json();
}
export const sendDelete = async (url: string) : Promise<void> => {
    await makeRequest(url, 'DELETE');
};

// The helpers above throw parsed non-2xx error BODIES as plain strings (see sendReuest), so a
// react-query onError handler typed to Error that reads `.message` gets undefined and renders
// an empty banner. Normalize at the call site with this instead of reading `.message` directly.
export const getRequestErrorMessage = (err: unknown): string => {
    if (typeof err === 'string' && err) return err;
    if (err instanceof Error && err.message) return err.message;
    return 'Something went wrong';
};

export function appendQueryParams(url: string, params?: {[key: string]: string|string[]|number|Date|boolean|null|undefined}) {
    if (params == null || params == undefined) return url;

    for (const key in params) {
        url = addOptionalQueryParam(url, key, params[key]);
    }
    
    return url;
}

export function addOptionalQueryParam(oldUrl: string, queryParamName: string, queryParamValue: string|string[]|number|Date|boolean|null|undefined): string {
    if (queryParamValue === null || queryParamValue === undefined) {
        return oldUrl;
    }

    if (queryParamValue instanceof Date) {
        queryParamValue = queryParamValue.toISOString();
    }

    if (Array.isArray(queryParamValue)) {
        return queryParamValue.reduce((url, value) => addOptionalQueryParam(url, queryParamName, value), oldUrl);
    }

    const delimiter = oldUrl.indexOf('?') >= 0 ? '&' : '?';
    return oldUrl + delimiter + queryParamName + '=' + encodeURIComponent(queryParamValue);
}

export declare type HTTPMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export declare type ContentType = "json" | "form" | "bson" | "msgpack";

const makeRequest = async (url: string, method: HTTPMethod, body?: any, type: ContentType = "json", accept?: string, signal?: AbortSignal) => {
    const request = initRequest(method, type, body, accept, signal);
    return await sendReuest(url, request);
};

const initRequest = (method: HTTPMethod, type: ContentType, body?: any, accept?: string, signal?: AbortSignal) => ({
    method: method,
    credentials: "include",
    signal,

    ...(type === "json" && {
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
            'Accept': accept,
        } as HeadersInit,
    }),

    ...(type === "bson" && {
        body: Buffer.from(BSON.serialize(body)),
        headers: {
            'Content-Type': 'application/bson',
            'Accept': accept,
        } as HeadersInit,
    }),

    ...(type === "msgpack" && {
        body: Buffer.from(packMsgpack(body)),
        headers: {
            'Content-Type': 'application/msgpack',
            'Accept': accept,
        } as HeadersInit,
    }),

    ...(type === "form" && {
        body: initFormData(body),
        // default Content-Type is multipart/form-data
        // and it will calculate the mandatory boundary automatically
    }),
}) as RequestInit;

const sendReuest = async (url: string, request: RequestInit): Promise<Response> => {
    const reply = await fetch(url, request).catch((error) => {
        // Check if the error is a network connectivity issue
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (
            errorMessage.includes('ERR_INTERNET_DISCONNECTED') ||
            errorMessage.includes('Failed to fetch') ||
            errorMessage.includes('NetworkError') ||
            errorMessage.includes('network error') ||
            errorMessage.includes('Network request failed')
        ) {
            useApiConnectivityStore.getState().setHasConnectionIssue(true);
            console.error("Network connectivity issue detected", error);
        }
        throw error instanceof Error ? error : `Something went wrong: ${errorMessage}`;
    });

    if (reply.ok) {
        useApiConnectivityStore.getState().setHasConnectionIssue(false);
        const contentType = reply.headers.get('Content-Type');

        if (contentType?.includes('application/bson')) {
            // Create a new Response with transformed body
            const arrayBuffer = await reply.arrayBuffer();
            const bsonData = BSON.deserialize(Buffer.from(arrayBuffer));

            // Create a new Response with JSON data
            const jsonBlob = new Blob([JSON.stringify(bsonData)], {
                type: 'application/json'
            });

            return new Response(jsonBlob, {
                status: reply.status,
                statusText: reply.statusText,
                headers: reply.headers
            });
        }

        if (contentType?.includes('application/msgpack')) {
            // Create a new Response with transformed body
            const arrayBuffer = await reply.arrayBuffer();
            const msgpackData = unpackMsgpack(Buffer.from(arrayBuffer));

            // Create a new Response with JSON data
            const jsonBlob = new Blob([JSON.stringify(msgpackData)], {
                type: 'application/json'
            });

            return new Response(jsonBlob, {
                status: reply.status,
                statusText: reply.statusText,
                headers: reply.headers
            });
        }

        return reply;
    }

    if (reply.body) {
        const text = await reply.text();
        throw tryParseErrorJson(text) || text;
    } else {
        console.error("Something went wrong", reply);
        throw `Something went wrong`;
    }
};

const tryParseErrorJson = (text: string) => {
    const json = tryParseJson(text);
    if (!json) return null;
    // Backend error shapes vary: ProblemDetails ({title, errors}), and a plain {error} or
    // {message} used by many endpoints (e.g. POST /api/nxhub/alerts returns {"error": "..."}).
    // Collect whichever are present and drop empties so we never produce the literal "undefined".
    const parts = [
        json.title,
        json.error,
        json.message,
        ...Object.keys(json.errors ?? {}).map((key: string) => key ? `${key}: ${json.errors[key]}` : json.errors[key]),
    ].filter((p) => p != null && p !== '');
    return parts.length ? parts.join('\n') : null;
}

const tryParseJson = (text: string) => {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

const initFormData = (body: any) => {
    const formData = new FormData();
    for (const key in body) {
        if (body[key] instanceof Array) {
            for (const item of body[key]) {
                appendFormData(formData, key, item);
            }
        } else {
            appendFormData(formData, key, body[key]);
        }
    }
    return formData;
};

const appendFormData = (formData: FormData, key: string, value: any) => {
    if (value instanceof File) {
        console.log(key, value instanceof File, value.name);
        formData.append(key, value, value.name);
    } else {
        formData.append(key, value);
    }
}
